// src/app/core/guards/tier-access.guard.ts
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { MeService } from '../shared/me.service';
import { CityService, CityOption } from '../shared/city.service';
import { LocationSelectionService } from '../../pages/service/location-selection.service';
import { T0BaselineService } from '../shared/t0-model/t0-baseline.service';
import { AnalysisStore } from '../shared/t0-model/analysis.store';

type Tier = 't0' | 't3' | 't6';

@Injectable({ providedIn: 'root' })
export class TierAccessGuard implements CanActivate {
  private t0KickoffStarted = false;
  private meEnsureStarted = false;

  constructor(
    private router: Router,
    private me: MeService,
    private citySvc: CityService,
    private locSel: LocationSelectionService,
    private baseline: T0BaselineService,
    private store: AnalysisStore
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree | Promise<boolean | UrlTree> {
    const requiredTier = (route.data?.['tier'] as Tier | undefined) ?? 't0';

    const enableMeValidation =
      (route.data?.['enableMeValidation'] as boolean | undefined) ??
      ((): boolean => {
        try {
          const v = sessionStorage.getItem('enableMeValidation');
          return v ? v === 'true' : true;
        } catch {
          return true;
        }
      })();

    if (!enableMeValidation) {
      if (requiredTier === 't0') this.kickoffT0BaselineLoadNonBlocking();
      return true;
    }

    const allowedTiers = this.readAllowedTiersFromSession();

    // If /me hasn’t populated yet, do a one-time async ensure, then re-check.
    if (!allowedTiers.length) {
      return this.ensureMeThenDecide(requiredTier);
    }

    if (allowedTiers.includes(requiredTier)) {
      if (requiredTier === 't0') this.kickoffT0BaselineLoadNonBlocking();
      return true;
    }

    return this.router.parseUrl('/home');
  }

  private async ensureMeThenDecide(requiredTier: Tier): Promise<boolean | UrlTree> {
    try {
      // avoid spamming /me on rapid navigations
      if (!this.meEnsureStarted) {
        this.meEnsureStarted = true;
        const sessionId = this.ensureSessionId();
        const email =
          (sessionStorage.getItem('userEmail') ||
            sessionStorage.getItem('userEmailFromMe') ||
            '').trim();

        await this.me.checkAllowlist({ email, sessionId });
      }

      const allowedTiers = this.readAllowedTiersFromSession();
      if (!allowedTiers.length) return this.router.parseUrl('/home');

      if (allowedTiers.includes(requiredTier)) {
        if (requiredTier === 't0') this.kickoffT0BaselineLoadNonBlocking();
        return true;
      }

      return this.router.parseUrl('/home');
    } catch (e) {
      console.error('[TierAccessGuard] /me ensure failed:', e);
      return this.router.parseUrl('/home');
    } finally {
      // allow future retries if needed
      this.meEnsureStarted = false;
    }
  }

  private kickoffT0BaselineLoadNonBlocking(): void {
    if (this.t0KickoffStarted) return;
    if (this.store.getRunsSnapshot().length) return;

    this.t0KickoffStarted = true;

    queueMicrotask(() => {
      this.runT0Bootstrap().catch((e) => {
        console.error('[TierAccessGuard] T0 baseline bootstrap failed:', e);
      });
    });
  }

  private async runT0Bootstrap(): Promise<void> {
    if (this.store.getRunsSnapshot().length) return;

    const sessionId = this.ensureSessionId();
    const email =
      (sessionStorage.getItem('userEmail') ||
        sessionStorage.getItem('userEmailFromMe') ||
        '').trim();

    // 1) /me (safe; MeService caches)
    await this.me.checkAllowlist({ email, sessionId });

    // 2) /city (filtered by allowlist)
    const cities = await firstValueFrom(this.citySvc.getAll());
    if (!cities.length) return;

    // 3) ensure city selected
    if (!this.locSel.getSelected()?.city) {
      const chosen = this.pickDefaultCity(cities);
      if (chosen) this.commitCity(chosen);
    }

    const city = this.locSel.getSelected()?.city || '';
    if (!city) return;

    // 4) baseline
    await this.baseline.resetAndReloadBaselineForCity(city);
  }

  private ensureSessionId(): string {
    const key = 'session_id';
    let id = sessionStorage.getItem(key) || '';
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      try {
        sessionStorage.setItem(key, id);
      } catch {}
    }
    return id;
  }

  private readAllowedTiersFromSession(): Tier[] {
    try {
      const raw = sessionStorage.getItem('allowedTiersFromMe');
      const parsed = raw ? JSON.parse(raw) : null;
      const arr = Array.isArray(parsed) ? parsed : [];
      const norm = arr.map((x: any) => String(x).trim().toLowerCase());
      return norm.filter((x: string) => x === 't0' || x === 't3' || x === 't6') as Tier[];
    } catch {
      return [];
    }
  }

  private pickDefaultCity(items: CityOption[]): CityOption | null {
    const prev = this.locSel.getSelected();
    if (prev?.country && prev?.city) {
      const match = items.find((x) => this.eq(x.country, prev.country) && this.eq(x.city, prev.city));
      if (match) return match;
    }

    const assigned = (sessionStorage.getItem('cityNameFromMe') || '').trim();
    if (assigned) {
      const match = items.find((x) => this.eq(x.city, assigned));
      if (match) return match;
    }

    return items[0] ?? null;
  }

  private commitCity(sel: CityOption): void {
    this.locSel.setSelected({
      country: sel.country,
      city: sel.city,
      cityId: sel.id,
      t0Available: sel.availability.t0,
      t3Available: sel.availability.t3,
      t6Available: sel.availability.t6, // FIXED
      raw: sel.raw
    });
  }

  private eq(a: string, b: string): boolean {
    return (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
  }
}